import type { MouseEventHandler, PropsWithChildren } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import styled from 'styled-components';
import { Box, theme } from '@frameio/vapor';

const THUMB_DEFAULT_WIDTH = 236;
const THUMB_DEFAULT_HEIGHT = 133;
export const DETAIL_PANE_MAX_WIDTH = 276;
export const CREATOR_NAME_MAX_WIDTH = 110;

type DetailPaneProps = {
  onDoubleClick?: MouseEventHandler<HTMLDivElement>;
};

export const DetailPane = ({
  children,
  onDoubleClick,
}: PropsWithChildren<DetailPaneProps>) => {
  return (
    <Wrapper data-testid="detailpane-wrapper" onDoubleClick={onDoubleClick}>
      <AnimatePresence>
        <InnerContainer {...crossFade}>{children}</InnerContainer>
      </AnimatePresence>
    </Wrapper>
  );
};

export const ThumbnailLinkContainer = ({ children }: PropsWithChildren) => (
  <Box
    alignItems="center"
    borderRadius={8}
    display="flex"
    justifyContent="center"
    paddingTop={48}
  >
    {children}
  </Box>
);

const Wrapper = styled(Box)`
  border-radius: 0 0 ${theme.radii[8]} ${theme.radii[8]};
  display: none;

  ${theme.mediaQueries.tablet} {
    display: flex;
    height: 100%;
    justify-content: center;
    width: 50%;
  }
`;

const InnerContainer = styled(motion.div)`
  width: 100%;
`;

export const ThumbContainer = styled.div`
  align-items: center;
  display: flex;
  height: ${THUMB_DEFAULT_HEIGHT}px;
  justify-content: center;
  width: ${THUMB_DEFAULT_WIDTH}px;
`;

export const ProjectOrFolderThumbContainer = styled(ThumbContainer)`
  background: ${theme.colors.components.filetype.folder.gradient},
    ${theme.colors.background.secondary};
  border-radius: ${theme.radii[8]};
`;

export const DetailContainer = styled.div`
  margin: 0 auto;
  max-width: ${DETAIL_PANE_MAX_WIDTH}px;
  overflow: hidden;
`;

export const MetaContainer = styled.div`
  margin: 0 auto;
  max-width: ${DETAIL_PANE_MAX_WIDTH}px;
  overflow: hidden;
  padding-top: ${theme.spaces[24]};
`;

export const TextWrapper = styled.span`
  overflow: hidden;
  padding: 0 ${theme.spaces[8]};
  width: 100%;
`;

export const FileNameAnchor = styled.span`
  display: flex;
  grid-gap: ${theme.spaces[8]};
  justify-content: center;
  padding-top: ${theme.spaces[24]};
  text-decoration: none;
`;

const crossFade = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
  transition: {
    duration: 0.2,
    ease: theme.transitions.curves.primary,
    delay: 0.2,
  },
};
